import { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { createPortal } from 'react-dom';
import { api } from '../../lib/apiClient';
import { useAuthStore } from '../../store/authStore';
import { OrnamentDivider } from '../ui/OrnamentDivider';
import type { User } from '../../types/user';

export function DisplayNameModal() {
  const { needsDisplayName, setUser, setNeedsDisplayName } = useAuthStore();
  const [name, setName] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (needsDisplayName) {
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [needsDisplayName]);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    const trimmed = name.trim();
    if (!trimmed) {
      setError('Введите игровое имя');
      return;
    }
    setSaving(true);
    setError('');
    try {
      const user = await api.post<User>('/users/me', { displayName: trimmed });
      setUser(user);
      setNeedsDisplayName(false);
    } catch {
      setError('Не удалось сохранить. Попробуйте ещё раз.');
      setSaving(false);
    }
  }

  return createPortal(
    <AnimatePresence>
      {needsDisplayName && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
          className="fixed inset-0 z-50 flex items-center justify-center bg-mafia-black/85 backdrop-blur-sm"
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 16 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 16 }}
            transition={{ duration: 0.25, ease: [0.22, 1, 0.36, 1] }}
            className="card-vintage w-full max-w-sm mx-4 p-8 shadow-crimson"
          >
            <p className="font-mono text-xs text-mafia-gold tracking-[0.3em] uppercase text-center mb-2">
              ◆ Добро пожаловать ◆
            </p>
            <h2 className="font-display text-2xl font-bold text-mafia-paper text-center mb-1">
              Ваше игровое имя
            </h2>
            <p className="text-mafia-paper-dim text-sm text-center mb-6">
              Под этим именем вас будут знать за игровым столом
            </p>

            <OrnamentDivider className="mb-6" variant="dim" />

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <input
                  ref={inputRef}
                  type="text"
                  value={name}
                  onChange={(e) => { setName(e.target.value); setError(''); }}
                  placeholder="Напр. Дон Корлеоне"
                  maxLength={40}
                  className="w-full bg-mafia-card border border-mafia-border text-mafia-paper placeholder-mafia-muted font-mono text-sm px-4 py-3 focus:outline-none focus:border-mafia-gold transition-colors"
                />
                {error && (
                  <p className="text-red-400 text-xs font-mono mt-1">{error}</p>
                )}
              </div>

              <button
                type="submit"
                disabled={saving}
                className="btn-gold-solid w-full py-3 text-sm disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {saving ? 'Сохранение...' : 'Сохранить и войти'}
              </button>
            </form>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>,
    document.body,
  );
}
